import React, { useEffect, useState } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, GitBranchPlus, Plus, Trash2, Loader2, Check, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const BundleSplitStation = () => {
  const navigate = useNavigate();
  const [sourceCode, setSourceCode] = useState('');
  const [source, setSource] = useState(null);
  const [splits, setSplits] = useState([
    { new_bundle_code: '', qty: '' },
    { new_bundle_code: '', qty: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);

  const lookupSource = async () => {
    if (!sourceCode.trim()) return;
    setSearching(true);
    try {
      const r = await apiClient.get(
        `/shirt-mfg/shirting-bundles/${sourceCode.trim().toUpperCase()}`,
      );
      if (r.data.status !== 'RECEIVED') {
        toast.error(`Bundle is ${r.data.status} — only RECEIVED can be split`);
        setSource(null);
        return;
      }
      setSource(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bundle not found');
      setSource(null);
    } finally {
      setSearching(false);
    }
  };

  const updateSplit = (i, key, value) => {
    const copy = [...splits];
    copy[i][key] = value;
    setSplits(copy);
  };

  const addSplit = () =>
    setSplits([...splits, { new_bundle_code: '', qty: '' }]);
  const removeSplit = (i) => setSplits(splits.filter((_, x) => x !== i));

  const totalQty = splits.reduce((a, s) => a + (parseFloat(s.qty) || 0), 0);

  const onSubmit = async () => {
    if (!source) { toast.error('Pick a source bundle first'); return; }
    if (splits.some((s) => !s.new_bundle_code || !s.qty)) {
      toast.error('All splits need bundle code + qty');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post('/shirt-mfg/shirting-bundles/split', {
        source_bundle_code: source.bundle_code,
        splits: splits.map((s) => ({
          new_bundle_code: s.new_bundle_code.toUpperCase(),
          qty: parseFloat(s.qty),
        })),
      });
      toast.success(
        `Split OK — ${res.data.new_bundles.length} new bundle(s), source SCRAPPED`,
      );
      setSourceCode(''); setSource(null);
      setSplits([{ new_bundle_code: '', qty: '' },
                 { new_bundle_code: '', qty: '' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Split failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StaffScanGate stationName="Shirt Mfg — Bundle Split">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-bundle-split">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sections/shirt-manufacturing')}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="bundle-split-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Bundle Split</h1>
            <p className="text-sm text-[#E8DCC8]">
              Source is SCRAPPED — new bundles inherit design/shade
            </p>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Source Bundle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={sourceCode}
                onChange={(e) => setSourceCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && lookupSource()}
                placeholder="Scan or paste bundle code…"
                data-testid="bundle-split-source-input"
              />
              <Button
                onClick={lookupSource} disabled={searching || !sourceCode}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="bundle-split-lookup-btn"
              >
                {searching
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {source && (
              <div className="rounded-md bg-emerald-50/50 border p-3 text-sm grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><span className="text-gray-500">Code:</span> <span className="font-mono">{source.bundle_code}</span></div>
                <div><span className="text-gray-500">Design:</span> {source.design_name}</div>
                <div><span className="text-gray-500">Shade:</span> {source.shade}</div>
                <div><span className="text-gray-500">Qty:</span> {source.declared_qty}</div>
                <div><span className="text-gray-500">Consignment:</span> {source.consignment_id || '—'}</div>
                <div><span className="text-gray-500">Status:</span> <Badge variant="outline" className="text-[10px]">{source.status}</Badge></div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">New Bundles</CardTitle>
              <Badge
                variant={source && totalQty === source.declared_qty
                  ? 'default' : 'outline'}
                data-testid="bundle-split-total-badge"
              >
                Total {totalQty}{source ? ` / ${source.declared_qty}` : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {splits.map((s, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>New bundle code #{i + 1}</Label>
                  <Input
                    value={s.new_bundle_code}
                    onChange={(e) => updateSplit(
                      i, 'new_bundle_code', e.target.value.toUpperCase(),
                    )}
                    placeholder="BUN-SHM-..."
                    data-testid={`bundle-split-new-code-${i}`}
                  />
                </div>
                <div className="w-32">
                  <Label>Qty</Label>
                  <Input
                    type="number" value={s.qty}
                    onChange={(e) => updateSplit(i, 'qty', e.target.value)}
                    data-testid={`bundle-split-new-qty-${i}`}
                  />
                </div>
                {splits.length > 2 && (
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => removeSplit(i)}
                    data-testid={`bundle-split-remove-${i}`}
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                )}
              </div>
            ))}
            <div className="flex justify-between">
              <Button
                variant="outline" size="sm" onClick={addSplit}
                data-testid="bundle-split-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add split
              </Button>
              <Button
                onClick={onSubmit}
                disabled={submitting || !source || splits.length < 2
                  || (source && totalQty !== source.declared_qty)}
                className="bg-[#1A1F2E] hover:bg-[#0F1320] text-white"
                data-testid="bundle-split-submit-btn"
              >
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <GitBranchPlus className="w-4 h-4 mr-2" />}
                Split & Scrap Source
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
    </StaffScanGate>
  );
};

export default BundleSplitStation;
